import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Duration = "1" | "3" | "6" | "12";

const BASIC_MONTHLY_PRICE = 197000;

const Plans = () => {
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState<Duration>("1");
  const [isLoading, setIsLoading] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [planExpiresAt, setPlanExpiresAt] = useState<Date | null>(null);
  const [futureExpiresAt, setFutureExpiresAt] = useState<Date | null>(null);

  useEffect(() => {
    const fetchUserPlan = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('plan, plan_expires_at')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          setUserPlan(profile.plan);
          if (profile.plan_expires_at) {
            setPlanExpiresAt(new Date(profile.plan_expires_at));
          }
        }
      }
    };

    fetchUserPlan();
  }, []);

  // Calculate future expiration date when duration changes
  useEffect(() => {
    if (planExpiresAt) {
      const future = new Date(planExpiresAt);
      future.setMonth(future.getMonth() + parseInt(selectedDuration));
      setFutureExpiresAt(future);
    } else {
      const future = new Date();
      future.setMonth(future.getMonth() + parseInt(selectedDuration));
      setFutureExpiresAt(future);
    }
  }, [selectedDuration, planExpiresAt]);

  const getDurationDiscount = (duration: Duration) => {
    switch (duration) {
      case "3": return 0.05;
      case "6": return 0.10;
      case "12": return 0.20;
      default: return 0;
    }
  };

  const calculatePrice = (basePrice: number, duration: Duration) => {
    const discount = getDurationDiscount(duration);
    const months = parseInt(duration);
    return Math.round((basePrice * months * (1 - discount)) / 1000) * 1000;
  };

  const calculateOriginalPrice = (basePrice: number, duration: Duration) => {
    const months = parseInt(duration);
    return basePrice * months;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("vi-VN").format(price);
  };

  const handleUpgrade = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("Vui lòng đăng nhập để nâng cấp");
        return;
      }

      const verificationCode = "MAI" + Math.random().toString(36).substring(2, 8).toUpperCase();
      const amount = calculatePrice(BASIC_MONTHLY_PRICE, selectedDuration);
      const months = parseInt(selectedDuration);
      const expiresAt = new Date(Date.now() + 15 * 60000).toISOString();

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert({
          user_id: session.user.id,
          verification_code: verificationCode,
          amount: amount,
          months: months,
          expires_at: expiresAt
        });

      if (transactionError) throw transactionError;
      navigate(`/nangcap?amount=${amount}&des=${verificationCode}`);
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error("Có lỗi xảy ra. Vui lòng thử lại sau.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container max-w-6xl py-4 px-2">
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { value: "12", label: "12 tháng", discount: "-20%" },
          { value: "6", label: "6 tháng", discount: "-10%" },
          { value: "3", label: "3 tháng", discount: "-5%" },
          { value: "1", label: "1 tháng" },
        ].map((duration) => (
          <Button
            key={duration.value}
            variant={selectedDuration === duration.value ? "default" : "outline"}
            onClick={() => setSelectedDuration(duration.value as Duration)}
            className="flex-1 min-w-[80px] h-auto py-2"
          >
            <div className="flex flex-col items-center">
              <span className="text-sm">{duration.label}</span>
              {duration.discount && (
                <span className="text-xs text-blue-500">{duration.discount}</span>
              )}
            </div>
          </Button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <div className="h-5 w-5 rounded-full bg-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Miễn phí</h2>
              <p className="text-sm text-muted-foreground">30 tin nhắn/ngày</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Các tính năng cơ bản:</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">•</span>
                  Trả lời tin nhắn tự động
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">•</span>
                  Viết nội dung đơn giản
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">•</span>
                  Hỗ trợ học tập cơ bản
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">•</span>
                  Trò chuyện và tư vấn cho bạn mọi chủ đề
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm text-muted-foreground line-through mb-1">Tin nhắn thoại</p>
              <p className="text-sm text-muted-foreground line-through mb-1">Gửi hình ảnh</p>
              <div className="text-sm text-muted-foreground line-through mb-1">
                <p className="mb-1">Truy cập các bot khác:</p>
                <ul className="pl-4 space-y-1">
                  <li className="text-muted-foreground">❤️ Mai Bot tư vấn tình yêu</li>
                  <li className="text-muted-foreground">🎓 Mai Bot dạy học</li>
                  <li className="text-muted-foreground">💼 Mai Bot trợ lý công việc</li>
                </ul>
              </div>
            </div>
          </div>
          {userPlan === 'free' ? (
            <Button className="w-full mt-4" variant="outline" disabled>
              Đang sử dụng
            </Button>
          ) : ( <div></div>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
              <div className="h-5 w-5 rounded-full bg-purple-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Pro</h2>
              <p className="text-sm text-muted-foreground">
                200 tin nhắn/ngày (gấp 6 lần gói Free)
              </p>
            </div>
          </div>
          <div className="mb-4">
            <div className="text-2xl font-bold">
              {formatPrice(calculatePrice(BASIC_MONTHLY_PRICE, selectedDuration))} đ
            </div>
            {selectedDuration !== "1" && (
              <div className="text-sm text-muted-foreground line-through">
                {formatPrice(calculateOriginalPrice(BASIC_MONTHLY_PRICE, selectedDuration))} đ
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              cho {selectedDuration} tháng
            </div>
            {planExpiresAt && (
              <div className="mt-2 text-sm">
                <p>Hết hạn hiện tại: {planExpiresAt.toLocaleDateString('vi-VN')}</p>
                {userPlan === 'basic' && (
                  <p className="text-green-500">
                    Sau khi gia hạn: {futureExpiresAt?.toLocaleDateString('vi-VN')}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-purple-500">•</span>
                10 tin nhắn thoại/ngày
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-500">•</span>
                15 tin nhắn có hình ảnh/ngày
              </li>
            </ul>
            <div>
              <p className="text-sm font-medium mb-2">Tạo nội dung chuyên sâu:</p>
              <ul className="space-y-1 text-sm pl-4">
                <li>-  Viết lách chuyên sâu </li>
                <li>- phân tích thông minh</li>
                <li>- nghiên cứu tức thời</li>
                <li>-chiến lược sáng tạo</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Truy cập các bot khác:</p>
              <ul className="pl-4 space-y-1 text-sm">
                <li>💜 Mai Bot tư vấn tình yêu</li>
                <li>🎓 Mai Bot dạy học</li>
                <li>💼 Mai Bot trợ lý công việc</li>
              </ul>
            </div>
          </div>

          {userPlan === 'basic' ? (
            <>
              <Button className="w-full mt-4" variant="outline" disabled>
                Đang sử dụng
              </Button>
              <Button 
                className="w-full mt-2 bg-purple-500 hover:bg-purple-600"
                onClick={handleUpgrade}
                disabled={isLoading}
              >
                {isLoading ? "Đang xử lý..." : "Gia hạn"}
              </Button>
            </>
          ) : (
            <Button 
              className="w-full mt-4 bg-purple-500 hover:bg-purple-600"
              onClick={handleUpgrade}
              disabled={isLoading}
            >
              {isLoading ? "Đang xử lý..." : "Nâng cấp"}
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Plans;
